'use es6';

import I18n from 'I18n';
import { flatten, isEqual, find, isEmpty } from 'underscore';
import { createSelector } from 'reselect';
import { getDefinition, getFormFieldGroups, getPostSubmissionOption, getFormFieldsByPropertyId, getFormStyle } from 'FormsUI/selectors/FormDefinition';
import PostSubmissionOptions from 'forms-utils-lib/constants/formDefinition/PostSubmissionOptions';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { Values } from 'FormsUI/constants/FormPreviewValues';
import { FormPreviewKeys } from 'FormsUI/lib/FormPreview';
import updateFormFieldKey from 'forms-utils-lib/formField/utils/updateFormFieldKey';
import FormFieldKeys from 'forms-utils-lib/constants/formField/FormFieldKeys';
import { PropertyObjectTypes } from 'FormsUI/constants/PropertyObjectTypes';
function getDefaultValue(formField) {
  const {
    fieldType,
    name
  } = formField;
  switch (fieldType) {
    case FieldTypes.NUMBER:
      return Values[fieldType];
    case FieldTypes.TEXT:
      if (name === 'email') {
        return I18n.text('editor.preview.knownEmail');
      }
      return I18n.text(Values[fieldType]);
    case FieldTypes.TEXTAREA:
      return I18n.text(Values[fieldType]);
    case FieldTypes.DATE:
      return I18n.moment().format('L');
    case FieldTypes.SINGLE_CHECKBOX:
      return 'true';
    case FieldTypes.CHECKBOX:
    case FieldTypes.RADIO:
    case FieldTypes.SELECT:
      {
        const maybeOption = find(formField.options, option => Boolean(option.value));
        return maybeOption ? maybeOption.value : '';
      }
    case FieldTypes.FILE:
    case FieldTypes.PHONE:
    default:
      return formField;
  }
}
function getFormPreview(state) {
  return state.formPreview;
}
export function getPrefilledFields(state) {
  return state.formPreview.get(FormPreviewKeys.PREFILLED_FIELDS);
}
export function getPreviewContact(state) {
  return getFormPreview(state).get(FormPreviewKeys.PREVIEW_CONTACT);
}
export const getFormDefinitionPreview = createSelector([getDefinition, getFormPreview, getPostSubmissionOption, getFormStyle], (formDefinition, _, postSubmissionOption, formStyle) => {
  const resetThankYouMessageOrPageRedirect = {};
  if (isEqual(postSubmissionOption, PostSubmissionOptions.THANK_YOU_MESSAGE)) {
    resetThankYouMessageOrPageRedirect.redirect = '';
  } else if (isEqual(postSubmissionOption, PostSubmissionOptions.REDIRECT_PAGE)) {
    resetThankYouMessageOrPageRedirect.inlineMessage = '';
  }
  return Object.assign({}, formDefinition, resetThankYouMessageOrPageRedirect, {
    style: formStyle
  });
});
export const getFormFieldsPreview = (formFieldGroups, formPreview, isDefault) => {
  const formFields = formFieldGroups.filter(formFieldGroup => formFieldGroup.default === isDefault).map(formFieldGroup => formFieldGroup.fields);
  return flatten(formFields).map(formField => {
    return updateFormFieldKey(formField, FormFieldKeys.IS_SELECTED, formPreview[formField.name]);
  });
};
export const getFormPreviewRenderState = createSelector(getFormPreview, preview => {
  return preview.get(FormPreviewKeys.REQUEST_STATE);
});
export const getFormPreviewPageStep = createSelector(getFormPreview, preview => preview.get(FormPreviewKeys.PAGE_STEP));
export const getMainFormFieldsPreview = createSelector([getFormFieldGroups, getPrefilledFields], (formFieldGroups, formPreview) => getFormFieldsPreview(formFieldGroups, formPreview, true));
export const getQueuedFormFieldsPreview = createSelector([getFormFieldGroups, getPrefilledFields], (formFieldGroups, formPreview) => getFormFieldsPreview(formFieldGroups, formPreview, false));
export const getAlreadyFilledFormFields = createSelector([getPrefilledFields, getFormFieldsByPropertyId, getPreviewContact], (formPreview, formFieldsByPropertyId, previewContact) => {
  const contactFormFieldByName = Object.values(formFieldsByPropertyId || {}).filter(formField => formField.propertyObjectType === PropertyObjectTypes.CONTACT).reduce((memo, formField) => Object.assign({}, memo, {
    [formField.name]: formField
  }), {});
  return Object.keys(contactFormFieldByName).reduce((memo, fieldName) => {
    // Check if prop present in contact
    const contactProp = !isEmpty(previewContact) ? previewContact.getProp(fieldName) : null;
    if (!isEmpty(contactProp)) {
      memo[fieldName] = contactProp;
      return memo;
    }
    const isChecked = formPreview.get(fieldName);
    if (!isChecked) {
      return memo;
    }
    memo[fieldName] = getDefaultValue(contactFormFieldByName[fieldName]);
    return memo;
  }, {});
});